import { useCallback } from 'react';
import type { ComponentProps } from 'react';

import type { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import { expVal, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import { LightNodeSerializer } from '../light/LightNodeSerializer.tsx';

import { AboveTheFoldReactSerializer } from './AboveTheFoldReactSerializer.tsx';
import { ProgressiveReactSerializer } from './ProgressiveReactSerializer.tsx';
import { useOnlySerializeFirstBatch } from './useOnlySerializeFirstBatch.ts';
import { useProgressiveRenderingConfig } from './useProgressiveRenderingConfig.ts';

type UseCreateSerializerProps = Parameters<typeof useProgressiveRenderingConfig>[0] & {
	adfContentId?: string;
	// Raw eligibility: normal View Page, after SSR/hydration.
	allowLightRendering?: boolean;
	// Printing bypasses LightNodeSerializer; legacy progressive serializers are unchanged.
	forceFullSerialization?: boolean;
	onContentComplete?: () => void;
};

/**
 * Shared-estimator gate enabled (unless progressive rendering is skipped):
 *
 * | Visible nodes | SSR                    | Client                              |
 * | ------------- | ---------------------- | ----------------------------------- |
 * | < 1,750       | Render all content     | Hydrate all content                 |
 * | >= 1,750      | First 2,160px batch    | Hydrate first batch; batch the rest |
 *
 * Nodes hidden in collapsed expands are not counted, as in `useProgressiveRenderingConfig`.
 * 1,750 approximates the observed p90 of 1,751 nodes, keeping roughly the smaller
 * 90% of pages on full SSR and hydration while batching larger pages.
 * 2,160px covers 99% of recorded screen heights across 18.1 million production
 * Confluence initial View Page loads in fe_performance, 19–25 September 2026 UTC.
 * Each batch targets that height with no buffer; complete top-level blocks may exceed it.
 */
const FULL_SSR_NODE_THRESHOLD = 1750;

type CreateSerializer = ComponentProps<typeof ADFRenderer>['createSerializer'];

export const useCreateSerializer = (props: UseCreateSerializerProps): CreateSerializer => {
	const {
		adfContentId,
		onContentComplete,
		skip,
		totalNodeCount,
		totalNodesHiddenFromView,
		allowLightRendering,
		forceFullSerialization,
	} = props;

	const { isComplexPageReason, batchSize } = useProgressiveRenderingConfig(props);

	// Initially, only serialize the first batch. This is a performance optimization to delay
	// serialization of the remaining batches until later. The optimization is disabled if there
	// are not enough batches to warrant it.
	//
	// For SSR, subsequent serializations never happen.
	// For SPA, subsequent serializations happen after the first batch is rendered.
	const { onlySerializeFirstBatch, onBatchRendered } = useOnlySerializeFirstBatch({
		adfContentId,
		onContentComplete,
	});

	const createComplexPageSerializer: NonNullable<CreateSerializer> = useCallback(
		(init) => {
			if (fg('platform_renderer_shared_height_estimator')) {
				const enableLightRendering = Boolean(
					allowLightRendering &&
					!onlySerializeFirstBatch &&
					!(
						typeof init.allowHeadingAnchorLinks === 'object' &&
						init.allowHeadingAnchorLinks.activeHeadingId
					) &&
					expVal('cc_enable_light_mode_progressive_rendering', 'isEnabled', false),
				);
				return new LightNodeSerializer(init, onlySerializeFirstBatch, onBatchRendered, {
					allowLightRendering: enableLightRendering,
					// Only sessions that can render light content are exposed to the experiment.
					allowVirtualizeTables:
						enableLightRendering && isExperimentEnabled('cc_light_mode_table_virtualization'),
				});
			}
			if (isComplexPageReason === 'TooManyExtensions') {
				return new AboveTheFoldReactSerializer(init, onlySerializeFirstBatch, onBatchRendered);
			} else {
				// Progressively renders ADF as described at https://hello.atlassian.net/wiki/spaces/~lmarinov/pages/5177285037/COMPLEXIT+Progressive+rendering+of+ADF
				return new ProgressiveReactSerializer(
					init,
					batchSize,
					onlySerializeFirstBatch,
					onBatchRendered,
				);
			}
		},
		[batchSize, isComplexPageReason, onlySerializeFirstBatch, onBatchRendered, allowLightRendering],
	);

	if (
		!skip &&
		(fg('platform_renderer_shared_height_estimator')
			? !forceFullSerialization &&
				totalNodeCount - totalNodesHiddenFromView >= FULL_SSR_NODE_THRESHOLD
			: isComplexPageReason)
	) {
		return createComplexPageSerializer;
	}
};
